import { gql } from "graphql-tag";
import apolloClient from "@/apollo";
import type { GetFilenames, GotFilenames } from "@/models/api/getFilenames";

const getFilenamesQuery = gql`
  query GetFilenames($input: GetFilenames) {
    getFilenames(input: $input) {
      __typename
      files
      hasReportedData
      service
      pageInfo {
        hasNextPage
        endCursor
        hasPreviousPage
        startCursor
      }
    }
  }
`;

export type GetFilenamesResponse = {
  success: boolean;
  results?: GotFilenames;
  message?: string;
};

/**
 * Function in charge of fetching the list of encoded parquet files on a given dateHour range.
 * @param payload - Object containing all the input variables of the query
 * @returns Resolved promise with the success flag and the query results or the error message
 */
export default async function getFilenames(
  payload: GetFilenames
): Promise<GetFilenamesResponse> {
  try {
    const result = await apolloClient.query({
      query: getFilenamesQuery,
      variables: {
        input: {
          startDateHour: payload.startDateHour,
          startMinute: payload.startMinute,
          endDateHour: payload.endDateHour,
          endMinute: payload.endMinute,
          service: payload.service,
          pageCursor: payload.pageCursor,
          pageSize: 168,
        },
      },
      fetchPolicy: "no-cache",
    });
    const gotFilenames: GotFilenames = result.data.getFilenames;
    let currentFiles = gotFilenames.files;
    if (gotFilenames.pageInfo.hasNextPage) {
      // Connect the current files with the next recursively
      const nextPageFiles = await getFilenames({
        ...payload,
        pageCursor: gotFilenames.pageInfo.endCursor,
      });
      currentFiles = currentFiles.concat(
        nextPageFiles.results?.files as string[]
      );
    }

    return {
      success: true,
      results: { ...gotFilenames, files: currentFiles },
    };
  } catch (e: any) {
    const error = e.graphQLErrors ? e.graphQLErrors[0] : e;
    return {
      success: false,
      message: error.message,
    };
  }
}
